// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid } from "@components";
import { formatDateTime } from "@transformers/date";
import type { ReferralHistoryRecord } from "@modules/employee/reducer/employee/types";
import actionCreators from "@modules/reporting/actionCreator";
import { hasWellbeingReferralDetailsAccess } from "@modules/account/selectors";
import type { State } from "@store/reducers";

export const colorMap = { PENDING: "red", ACTIVE: "pumpkin-orange" }; // PENDING = Awaiting supplier

type OwnProps = $Exact<{
  clientId: number,
  data: Array<Object>,
}>;

type PropsFromState = $Exact<{
  hasWellbeingReferralDetailsAccess: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getSupplierReferralDetails: typeof actionCreators.GetSupplierReferralDetails,
  getLogReferralDetails: typeof actionCreators.GetLogReferralDetails,
  setReferralCommonData: typeof actionCreators.SetReferralCommonData,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class ReferralHistoryGrid extends React.Component<Props> {
  handleLogClick = (row: Object) => () => {
    this.props.setReferralCommonData({ ...row, clientId: this.props.clientId });
    this.props.getLogReferralDetails({
      supplierReferralId: row.referralId,
      clientId: this.props.clientId,
    });
  };
  handleDetailsClick = (row: Object) => () => {
    this.props.getSupplierReferralDetails({
      supplierReferralId: row.referralId,
      clientId: this.props.clientId,
      referralType: null,
    });
  };
  get columnDefs() {
    const defaultColumnDes = [
      {
        name: this.props.t("REFERRAL_ID"),
        key: "referralId",
        frozen: "left",
      },
      {
        name: this.props.t("REFERRAL_TYPE"),
        key: "referralType",

        renderCell: ({ row }: { row: ReferralHistoryRecord }) => this.props.t(row.referralType),
      },
      {
        name: this.props.t("DATE_REFERRED"),
        key: "dateReferred",

        renderCell: ({ row }: { row: ReferralHistoryRecord }) => formatDateTime(row.dateReferred),
      },
      {
        name: this.props.t("REFERRED_BY"),

        renderCell: ({ row }: { row: ReferralHistoryRecord }) => {
          return `${row.referredByFirstName} ${row.referredByLastName}`;
        },
      },
      {
        name: this.props.t("REASON_FOR_REFERRAL"),
        key: "reason",
      },
      {
        name: this.props.t("STATUS"),
        key: "status",

        renderCell: ({ row }: { row: ReferralHistoryRecord }) => {
          return (
            <span
              title={this.props.t(row.status)}
              className={`text-color ${colorMap[row.status] ? colorMap[row.status] : "positive"}`}
            >
              {this.props.t(row.status)}
            </span>
          );
        },
      },
      {
        name: this.props.t("DATE_CLOSED"),
        key: "dateClosed",

        renderCell: ({ row }: { row: ReferralHistoryRecord }) => formatDateTime(row.dateClosed),
      },
      {
        name: this.props.t("RTW_DATE_AT_REFERRAL"),
        key: "rtwDateAtReferral",

        renderCell: ({ row }: { row: ReferralHistoryRecord }) =>
          formatDateTime(row.rtwDateAtReferral),
      },
      {
        name: this.props.t("FINAL_RTW_DATE"),
        key: "rtwDate",
        renderCell: ({ row }: { row: ReferralHistoryRecord }) => formatDateTime(row.rtwDate),
      },
      {
        name: this.props.t("DAYS_CHANGE"),

        key: "rtwDaysChanged",
        renderCell: ({ row }: { row: ReferralHistoryRecord }) => {
          let textColor = "";
          if (row.rtwDaysChanged === null) return "";
          if (row.rtwDaysChanged < 0) textColor = "positive";
          else if (row.rtwDaysChanged === 0) textColor = "pumpkin-orange ";
          else if (row.rtwDaysChanged > 0) textColor = "negative";
          return (
            <span title={row.rtwDaysChanged} className={`font-weight-bold text-color ${textColor}`}>
              {row.rtwDaysChanged}
            </span>
          );
        },
      },
    ];

    if (this.props.hasWellbeingReferralDetailsAccess) {
      return [
        ...defaultColumnDes,
        {
          name: this.props.t("DETAILS"),

          renderCell: ({ row }: *) => {
            return (
              <span onClick={this.handleDetailsClick(row)} className="material-icons pointer">
                search
              </span>
            );
          },
        },
        {
          name: this.props.t("LOG"),

          renderCell: ({ row }: *) => {
            return (
              <span onClick={this.handleLogClick(row)} className="material-icons pointer">
                search
              </span>
            );
          },
        },
      ];
    }

    return defaultColumnDes;
  }

  get rowData() {
    return this.props.data || [];
  }

  render() {
    return <GSGrid columns={this.columnDefs} rows={this.rowData} />;
  }
}
const mapStateToProps = (state: State): PropsFromState => ({
  hasWellbeingReferralDetailsAccess: hasWellbeingReferralDetailsAccess(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getSupplierReferralDetails: actionCreators.GetSupplierReferralDetails,
  getLogReferralDetails: actionCreators.GetLogReferralDetails,
  setReferralCommonData: actionCreators.SetReferralCommonData,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(ReferralHistoryGrid));
